<script lang="ts" setup>
import { plans } from '@thecompaniesapi/shared/client'

defineProps({
  hasFeatures: {
    default: true,
    type: Boolean,
  },
  hasPowers: {
    default: true,
    type: Boolean,
  },
})

const { companiesInMillions } = usePlatformStore()
const { t } = useI18n()
const { loadingCheckoutSession, openCheckoutSession } = useSubscription()

const paymentInterval = ref(0)

const availablePlans = ref<SubscriptionPlanAvailablePlan[]>([
  {
    credits: plans.startup.credits,
    features: [
      {
        icon: getIcon('check'),
        label: t('features.accessAllFeatures'),
        type: 'features',
      },
      {
        icon: getIcon('companies'),
        label: t('features.accessAllCompanies', { companiesInMillions }),
        type: 'companies',
      },
      {
        icon: getIcon('creditCard'),
        label: t('features.creditsOnDemand', { price: numbro(plans.startup.pricing.additionalCredits) }),
        type: 'creditsOnDemand',
      },
      {
        icon: getIcon('rateLimit'),
        label: t('reqPerSecond', { count: numbro(plans.startup.restrictions.rateLimitPerSec) }),
        type: 'rateLimit',
      },
    ],
    icon: getIcon('startup'),
    name: 'startup',
    power: 'magnifier',
    pricing: {
      monthly: plans.startup.pricing.monthly,
      yearly: plans.startup.pricing.yearly,
    },
  },
  {
    badge: { label: t('save', { percentage: 37 }) },
    credits: plans.scaleup.credits,
    features: [
      {
        icon: getIcon('check'),
        label: t('features.accessAllFeatures'),
        type: 'features',
      },
      {
        icon: getIcon('companies'),
        label: t('features.accessAllCompanies', { companiesInMillions }),
        type: 'companies',
      },
      {
        icon: getIcon('creditCard'),
        label: t('features.creditsOnDemand', { price: numbro(plans.scaleup.pricing.additionalCredits) }),
        type: 'creditsOnDemand',
      },
      {
        icon: getIcon('rateLimit'),
        label: t('reqPerSecond', { count: numbro(plans.scaleup.restrictions.rateLimitPerSec) }),
        type: 'rateLimit',
      },
    ],
    icon: getIcon('scaleup'),
    name: 'scaleup',
    power: 'king',
    pricing: {
      monthly: plans.scaleup.pricing.monthly,
      yearly: plans.scaleup.pricing.yearly,
    },
  },
  {
    badge: { label: t('save', { percentage: 40 }) },
    credits: plans.enterprise.credits,
    features: [
      {
        icon: getIcon('check'),
        label: t('features.accessAllFeatures'),
        type: 'features',
      },
      {
        icon: getIcon('companies'),
        label: t('features.accessAllCompanies', { companiesInMillions }),
        type: 'companies',
      },
      {
        icon: getIcon('creditCard'),
        label: t('features.creditsOnDemand', { price: numbro(plans.enterprise.pricing.additionalCredits) }),
        type: 'creditsOnDemand',
      },
      {
        icon: getIcon('rateLimit'),
        label: t('reqPerSecond', { count: numbro(plans.enterprise.restrictions.rateLimitPerSec) }),
        type: 'rateLimit',
      },
    ],
    icon: getIcon('enterprise'),
    name: 'enterprise',
    power: 'file',
    pricing: {
      monthly: plans.enterprise.pricing.monthly,
      yearly: plans.enterprise.pricing.yearly,
    },
  },
])

const paymentIntervals = computed((): UTabItem[] => {
  return [
    {
      icon: getIcon('calendarMonth'),
      label: t('payMonthly'),
      value: 'monthly',
    },
    {
      icon: getIcon('calendar'),
      label: t('payAnnually'),
      value: 'yearly',
    },
  ]
})

function getButtonConfigForPlan(plan: SubscriptionPlanAvailablePlan) {
  return {
    click: () => openCheckoutSession(plan.name, paymentInterval.value === 0 ? 'monthly' : 'yearly'),
    color: 'white',
    icon: plan?.icon,
    label: t('choose', { planName: t(`plans.${plan.name}`) }),
    loading: loadingCheckoutSession.value === plan.name,
    ui: {
      icon: {
        base: plan.name === 'scaleup'
          ? 'text-yellow-600 dark:text-yellow-400'
          : 'text-indigo-600 dark:text-indigo-400',

      },
    },
  } as UButton
}
</script>

<template>
  <div class="flex select-none flex-col items-center">
    <div
      class="mb-6 flex flex-col justify-center"
      :class="{
        'lg:mb-10': !hasPowers,
        'lg:mb-32': hasPowers,
      }"
    >
      <UTabs
        v-model="paymentInterval"
        :items="paymentIntervals"
        :ui="{
          list: {
            base: 'p-2',
            height: 'h-fit',
            background: 'gradient-texture-light',
            tab: {
              height: 'h-8 md:h-10',
              icon: 'size-5',
              padding: 'px-3 md:px-4',
              size: 'text-xs md:text-base',
              active: 'gradient-texture shadow-sm dark:shadow-md',
            },
            marker: {
              background: 'gradient-texture shadow-sm dark:shadow-md',
            },
          },
        }"
      />

      <div class="my-1.5 flex items-center justify-center">
        {{ t('twoMonthsFree') }}

        <UIcon
          class="-mt-0.5 ml-2 size-5 flex-shrink-0"
          :name="getIcon('arrowPointingTop')"
        />
      </div>
    </div>

    <div class="grid w-full grid-cols-1 gap-6">
      <UPricingGrid>
        <UPricingCard
          v-for="plan of availablePlans"
          :key="plan.name"
          align="top"
          :badge="plan.badge"
          :button="getButtonConfigForPlan(plan)"
          :cycle="t('globals.slashMonth')"
          :price="`$${numbro(paymentInterval === 0 ? plan.pricing.monthly : Math.floor(plan.pricing.yearly / 12))}`"
          :scale="plan.name === 'scaleup'"
          :title="t(`plans.${plan.name}`)"
        >
          <template #description>
            <BasePowerHead
              v-if="hasPowers"
              class="absolute hidden lg:left-1/2 lg:block lg:-translate-x-1/2 lg:transform"
              :class="{
                '-right-4 -top-6 lg:-top-12 lg:right-auto': plan.name !== 'scaleup',
                '-right-4 -top-12 lg:-top-20 lg:right-auto': plan.name === 'scaleup',
              }"
              :power="plan.power"
            />

            <BaseCoin
              class="mt-3"
              size="md"
            >
              <div class="flex items-center">
                {{ plan.credits }} {{ t('globals.credit', plan.credits) }} {{ t('perMonth') }}

                <UTooltip
                  :popper="{ placement: 'top' }"
                  :text="t('creditsExplanation')"
                >
                  <UIcon
                    class="size-4.5 ml-2 flex-shrink-0"
                    :name="getIcon('info')"
                    :ui="{ base: 'text-gray-700 dark:text-gray-300' }"
                  />
                </UTooltip>
              </div>
            </BaseCoin>

            <p class="-mb-3 mt-3 flex items-start text-sm text-gray-600 dark:text-gray-400">
              <UIcon
                class="mr-2 mt-0.5 size-5 flex-shrink-0"
                :name="paymentInterval === 1 ? 'solar:card-linear' : 'solar:card-transfer-linear'"
              />

              {{ paymentInterval === 1 ? t('totalBilled', { price: numbro(plan.pricing.yearly) }) : t('billedEveryMonth') }}
            </p>
          </template>

          <template
            v-if="hasFeatures"
            #bottom
          >
            <div class="gradient-texture-light flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-100 p-4 dark:border-gray-800 dark:bg-gray-900">
              <div
                v-for="feature of plan.features"
                :key="feature.label"
              >
                <div class="flex items-center">
                  <UIcon
                    class="mr-2 mt-0.5 flex-shrink-0"
                    :name="feature.icon"
                  />

                  <span class="flex-1 text-sm">
                    {{ feature.label }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </UPricingCard>
      </UPricingGrid>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "billedEveryMonth": "Billed every month",
    "choose": "Choose {planName}",
    "creditsExplanation": "Each enrichment requires 1 credit",
    "features": {
      "accessAllCompanies": "Access all {companiesInMillions}M companies",
      "accessAllCompaniesDetails": "All subscriptions get a full access to our database of companies and its daily updates.",
      "accessAllFeatures": "Access all API features",
      "creditsOnDemand": "{price}$ for 1,000 additional credits",
      "signals": "Get access to signals",
      "signalsDetails": "Get notified about important events happening with companies you track or are interested in.",
      "similar": "Find similar companies",
      "similarDetails": "Reveal companies similar to a specific company or a group of companies like your customers or competitors.",
      "standardDatapoints": "Standard datapoints",
      "standardDatapointsDetails": "This plan includes all standard datapoints for company enrichment."
    },
    "payAnnually": "Pay annually",
    "payMonthly": "Pay monthly",
    "perMonth": "per month",
    "reqPerSecond": "{count} requests per second",
    "save": "Save {percentage}%",
    "totalBilled": "Total billed: ${price}",
    "twoMonthsFree": "Two months free"
  },
  "es": {
    "billedEveryMonth": "Facturado cada mes",
    "choose": "Elegir {planName}",
    "creditsExplanation": "Cada enriquecimiento requiere 1 crédito",
    "features": {
      "accessAllCompanies": "Accede a todas las {companiesInMillions}M de empresas",
      "accessAllCompaniesDetails": "Todas las suscripciones obtienen acceso completo a nuestra base de datos de empresas y sus actualizaciones diarias.",
      "accessAllFeatures": "Accede a todas las características de la API",
      "creditsOnDemand": "{price}$ por 1,000 créditos adicionales",
      "signals": "Obtén acceso a señales",
      "signalsDetails": "Recibe notificaciones sobre eventos importantes que ocurren con las empresas que sigues o que te interesan.",
      "similar": "Encuentra empresas similares",
      "similarDetails": "Descubre empresas similares a una empresa específica o a un grupo de empresas como tus clientes o competidores.",
      "standardDatapoints": "Puntos de datos estándar",
      "standardDatapointsDetails": "Este plan incluye todos los puntos de datos estándar para el enriquecimiento de empresas."
    },
    "payAnnually": "Pagar anualmente",
    "payMonthly": "Pagar mensualmente",
    "perMonth": "por mes",
    "reqPerSecond": "{count} peticiones por segundo",
    "save": "Ahorra {percentage}%",
    "totalBilled": "Total facturado: ${price}",
    "twoMonthsFree": "Dos meses gratis"
  },
  "fr": {
    "billedEveryMonth": "Facturé chaque mois",
    "choose": "Choisir {planName}",
    "creditsExplanation": "Chaque enrichissement nécessite 1 crédit",
    "features": {
      "accessAllCompanies": "Accéder aux {companiesInMillions}M d'entreprises",
      "accessAllCompaniesDetails": "Tous les abonnements donnent un accès complet à notre base de données d'entreprises et à ses mises à jour quotidiennes.",
      "accessAllFeatures": "Accéder à toutes les fonctionnalités de l'API",
      "creditsOnDemand": "{price}$ pour 1,000 crédits supplémentaires",
      "signals": "Accédez aux signaux",
      "signalsDetails": "Soyez informé des événements importants se produisant avec les entreprises que vous suivez ou qui vous intéressent.",
      "similar": "Trouvez des entreprises similaires",
      "similarDetails": "Découvrez des entreprises similaires à une entreprise spécifique ou à un groupe d'entreprises tels que vos clients ou vos concurrents.",
      "standardDatapoints": "Points de données standard",
      "standardDatapointsDetails": "Ce plan inclut tous les points de données standard pour l'enrichissement des entreprises."
    },
    "payAnnually": "Payer à l'année",
    "payMonthly": "Payer au mois",
    "perMonth": "par mois",
    "reqPerSecond": "{count} requêtes par seconde",
    "save": "Save {percentage}%",
    "totalBilled": "Total facturé : ${price}",
    "twoMonthsFree": "Deux mois gratuits"
  }
}
</i18n>
